/**
 * Contact alias emails — extra addresses one client/lead contact uses (a personal Gmail
 * next to the work address, a billing inbox, ...), so Stripe, Fathom, Calendly, check-ins
 * etc. still land on the right account when that person shows up under another address.
 *
 * Stored on accounts/{id}:
 *   contactEmailAliases: [{ email, contactKey, contactName, addedAt, addedBy }]
 *   aliasEmails: [email]   // flat lowercase copy — the field integrations query (array-contains)
 * contactKey is PRIMARY_CONTACT_KEY for the CRM primary contact, else the portal user's uid.
 *
 * Mirrors functions/accountEmailAliases.js.
 */

export const PRIMARY_CONTACT_KEY = 'account-primary';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

export function isValidEmail(email) {
  return EMAIL_RE.test(normalizeEmail(email));
}

export function contactEmailAliasEntries(account) {
  return Array.isArray(account?.contactEmailAliases)
    ? account.contactEmailAliases.filter((a) => normalizeEmail(a?.email))
    : [];
}

/** Every alias email on an account, lowercase and deduped. */
export function accountAliasEmails(account) {
  const flat = Array.isArray(account?.aliasEmails) ? account.aliasEmails : [];
  const rich = contactEmailAliasEntries(account).map((a) => a.email);
  return [...new Set([...flat, ...rich].map(normalizeEmail).filter(Boolean))];
}

/**
 * Alias entries shown under one contact. Aliases whose contact is no longer on the account
 * (a removed portal user) fall back to the primary contact so they stay visible and removable.
 */
export function aliasesForContact(account, contactKey, liveContactKeys = []) {
  const live = new Set(liveContactKeys);
  return contactEmailAliasEntries(account).filter((a) => {
    const key = a.contactKey || PRIMARY_CONTACT_KEY;
    if (key === contactKey) return true;
    return contactKey === PRIMARY_CONTACT_KEY && !live.has(key);
  });
}

/** The two fields written together whenever the alias list changes. */
export function buildAliasFields(entries) {
  const contactEmailAliases = entries.map((a) => ({ ...a, email: normalizeEmail(a.email) }));
  return {
    contactEmailAliases,
    aliasEmails: [...new Set(contactEmailAliases.map((a) => a.email))],
  };
}
